import { Edit, Trash2, ExternalLink, MoreVertical, AlertCircle, CheckCircle, Copy, Link, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../utils/cn';
import { useState } from 'react';
import { isFeatureEnabled } from '../config/features';
import { copyToClipboard, getPublicStoryUrl } from '../utils/clipboard';
import { useToast } from '../contexts/ToastContext';
import { StatusDropdown } from './StatusDropdown';

interface StoryCardProps {
  story: {
    _id: string;
    title: string;
    one_line_brief: string;
    status: string;
    category_id?: { _id: string; name: string };
    subcat_id?: { _id: string; name: string };
    category_name?: string;
    subcategory_name?: string;
    noOfSlides: number;
    created_at: string;
    updated_at: string;
    public_token: string;
    tags: string[];
    isComplete?: boolean;
    incompleteSlides?: number;
  };
  onEdit: (storyId: string) => void;
  onDelete: (storyId: string) => void;
  onStatusChange: (storyId: string, status: string) => void;
  onView?: (story: { id: string; title: string }) => void;
}

/**
 * Mobile-friendly story card component
 * @param {StoryCardProps} props - Component props
 * @returns {JSX.Element} Story card
 */
export function StoryCard({ story, onEdit, onDelete, onStatusChange, onView }: StoryCardProps) {
  const [showActions, setShowActions] = useState(false);
  const { toast } = useToast();
  
  const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    published: 'bg-green-100 text-green-800',
    archived: 'bg-yellow-100 text-yellow-800',
  };

  /**
   * Handle copying public link to clipboard
   * @param {string} publicToken - Story's public token
   */
  const handleCopyLink = async (publicToken: string) => {
    const url = getPublicStoryUrl(publicToken);
    const success = await copyToClipboard(url);
    
    if (success) {
      toast({
        title: 'Success',
        description: 'Link copied to clipboard!'
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="card-mobile space-y-3">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-foreground truncate">
            {story.title}
          </h3>
          {story.one_line_brief && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {story.one_line_brief}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="touch-target -mr-2"
          onClick={() => setShowActions(!showActions)}
        >
          <MoreVertical className="h-5 w-5" />
        </Button>
      </div>

      {/* Metadata */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Category</span>
          <span className="font-medium">
            {story.category_id?.name || story.category_name || '-'} / {story.subcat_id?.name || story.subcategory_name || '-'}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Slides</span>
          <span className="font-medium">{story.noOfSlides}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Status</span>
          <div className="flex items-center gap-2">
            {isFeatureEnabled('UNIFIED_STATUS_DROPDOWN') ? (
              <StatusDropdown
                currentStatus={story.status}
                storyId={story._id}
                onStatusChange={onStatusChange}
                isStoryComplete={story.isComplete !== false}
                className="min-w-[140px]"
              />
            ) : (
              <Badge className={cn(statusColors[story.status as keyof typeof statusColors])}>
                {story.status}
              </Badge>
            )}
            {isFeatureEnabled('FLEXIBLE_DRAFTING') && story.status === 'draft' && story.isComplete !== undefined && (
              story.isComplete ? (
                <CheckCircle className="h-4 w-4 text-success" />
              ) : (
                <div className="flex items-center gap-1">
                  <AlertCircle className="h-4 w-4 text-warning" />
                  {story.incompleteSlides !== undefined && story.incompleteSlides > 0 && (
                    <span className="text-xs text-warning">({story.incompleteSlides})</span>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Tags */}
      {story.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {story.tags.filter(tag => tag).map((tag, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {/* Public Link - Show for published stories */}
      {story.status === 'published' && story.public_token && (
        <div className="border-t pt-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Link className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Public Link:</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyLink(story.public_token)}
                className="h-8 px-2"
              >
                <Copy className="h-3 w-3" />
              </Button>
              <a
                href={`/stories/public/${story.public_token}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-xs truncate max-w-[150px]"
              >
                /stories/public/{story.public_token.substring(0, 8)}...
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Actions - Always visible on mobile */}
      <div className={cn(
        "grid grid-cols-2 gap-2 pt-2 border-t transition-all duration-200",
        showActions ? "opacity-100 max-h-40" : "opacity-0 max-h-0 overflow-hidden"
      )}>
        <Button
          variant="outline"
          size="sm"
          className="touch-target"
          onClick={() => onEdit(story._id)}
          title="Edit story"
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          className="touch-target"
          onClick={() => onView?.({ id: story._id, title: story.title })}
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          className="touch-target text-destructive hover:text-destructive"
          onClick={() => onDelete(story._id)}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Delete
        </Button>

        {!isFeatureEnabled('UNIFIED_STATUS_DROPDOWN') && story.status === 'draft' && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="touch-target"
              onClick={() => onStatusChange(story._id, 'published')}
            >
              Publish
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="touch-target"
              onClick={() => onStatusChange(story._id, 'archived')}
            >
              Archive
            </Button>
          </>
        )}

        {story.status === 'published' && story.public_token && (
          <a
            href={`/stories/public/${story.public_token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center touch-target rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Public
          </a>
        )}
      </div>
    </div>
  );
}